import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

/*  tweddi — Homepage (/)
    Elevated "category-defining company" cut. Keeps the tweddi identity —
    cream ground, near-mono ink, apricot only as a rare accent, Inclusive Sans,
    woven monogram, circle-arrow CTAs — but adds the things a serious company's
    homepage has: a sharper hook, an animated proof/traction band, a real
    product glimpse (phone), a warm testimonial, objection-handling FAQ, and
    layered scroll choreography. Routing preserved: goToApp -> /join,
    /privacy, /terms.                                                         */

const CREAM   = '#FFFDF8';
const INK      = '#141313';
const MUTE     = '#6E6B62';
const APRICOT  = '#F4A26B';
const STONE    = '#ECE7DF';
const HAIR     = 'rgba(20,19,19,0.10)';

const Mark = ({ size = 26, mark = INK, bg = CREAM, style }) => (
  <svg viewBox="-60 -60 664 520" width={size} height={size * (520/664)} style={{ overflow: 'visible', display: 'block', flexShrink: 0, ...style }} aria-label="tweddi">
    <circle cx="200" cy="200" r="108" fill="none" stroke={mark} strokeWidth="66" />
    <circle cx="344" cy="200" r="108" fill="none" stroke={bg}   strokeWidth="108" />
    <circle cx="344" cy="200" r="108" fill="none" stroke={mark} strokeWidth="66" />
    <path d="M 206.01 307.83 A 108 108 0 0 0 282.00 270.28" fill="none" stroke={bg}   strokeWidth="108" />
    <path d="M 196.23 307.93 A 108 108 0 0 0 285.11 266.49" fill="none" stroke={mark} strokeWidth="66" />
  </svg>
);

const Wordmark = ({ light }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
    <Mark size={24} mark={light ? CREAM : INK} bg={light ? INK : CREAM} />
    <span style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.15rem', fontWeight: 600, letterSpacing: '-0.01em', color: light ? CREAM : INK }}>tweddi</span>
  </div>
);

const CircleArrow = ({ size = 34, dark = true }) => (
  <span className="tw-circle" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: 999, background: dark ? INK : CREAM, flexShrink: 0, transition: 'transform 0.25s cubic-bezier(.16,1,.3,1)' }}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={dark ? CREAM : INK} strokeWidth="2"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
  </span>
);

const Label = ({ children, light }) => (
  <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: light ? 'rgba(255,253,248,0.55)' : MUTE, margin: 0 }}>{children}</p>
);

/* A small animated count-up used in the proof band */
function Stat({ target, suffix = '', label }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting && !done.current) {
          done.current = true;
          const dur = 1400, t0 = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(target * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [target]);
  return (
    <div ref={ref} style={{ textAlign: 'center', flex: 1 }}>
      <p style={{ margin: 0, fontSize: 'clamp(2rem, 9vw, 2.75rem)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1, color: CREAM }}>{val.toLocaleString()}{suffix}</p>
      <p style={{ margin: '0.5rem 0 0', fontSize: '0.56rem', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,253,248,0.5)' }}>{label}</p>
    </div>
  );
}

/* The product glimpse — a stylised phone showing a discover card */
function PhoneGlimpse() {
  return (
    <div style={{ position: 'relative', width: '15rem', maxWidth: '78%', margin: '0 auto' }}>
      <div style={{ position: 'relative', borderRadius: '2.2rem', background: INK, padding: '0.5rem', boxShadow: '0 30px 60px -20px rgba(20,19,19,0.35), 0 8px 20px -8px rgba(20,19,19,0.2)' }}>
        <div style={{ borderRadius: '1.8rem', overflow: 'hidden', background: CREAM, aspectRatio: '9/17.5', position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {/* mini status row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.7rem 0 0.4rem' }}>
            <Mark size={18} mark={INK} bg={CREAM} />
          </div>
          {/* card photo area */}
          <div style={{ margin: '0.4rem 0.7rem 0', borderRadius: '1.1rem', overflow: 'hidden', flex: 1, background: `linear-gradient(160deg, ${STONE}, #DFD6C8)`, position: 'relative' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(120% 80% at 30% 20%, rgba(244,162,107,0.28), transparent 60%)' }} />
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '2rem 0.9rem 0.9rem', background: 'linear-gradient(to top, rgba(20,19,19,0.82), transparent)' }}>
              <p style={{ margin: 0, color: CREAM, fontSize: '1.15rem', fontWeight: 600, letterSpacing: '-0.02em' }}>Sofia, 29</p>
              <p style={{ margin: '0.15rem 0 0', color: 'rgba(255,253,248,0.7)', fontSize: '0.58rem', fontWeight: 500, letterSpacing: '0.06em' }}>Friend of the bride</p>
            </div>
          </div>
          {/* swipe controls */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.4rem', padding: '0.9rem 0 1.1rem' }}>
            <span style={{ width: '2.1rem', height: '2.1rem', borderRadius: 999, border: `1.5px solid ${HAIR}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: MUTE }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </span>
            <span className="pg-heart" style={{ width: '2.6rem', height: '2.6rem', borderRadius: 999, background: INK, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: CREAM }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
            </span>
          </div>
        </div>
      </div>
      {/* floating match pill */}
      <div className="pg-float" style={{ position: 'absolute', top: '18%', right: '-0.6rem', background: CREAM, border: `1px solid ${HAIR}`, borderRadius: 999, padding: '0.4rem 0.75rem', boxShadow: '0 10px 24px -8px rgba(20,19,19,0.25)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <span className="tw-beat" style={{ color: APRICOT, fontSize: '0.7rem' }}>♥</span>
        <span style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em', color: INK }}>It's a match</span>
      </div>
    </div>
  );
}

export default function MarketingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // "Get your code" is a HOST action: it starts the buy/create flow.
  // Existing hosts jump to their dashboard; everyone else is sent through
  // host signup (the isHostSignup flag makes AuthPage open the host register
  // tab, after which the user lands on /admin → pay €99 → create → code).
  const goToApp = () => {
    if (user?.is_host) { navigate('/admin'); return; }
    localStorage.setItem('isHostSignup', 'true');
    navigate('/auth');
  };
  const rootRef = useRef(null);
  const barRef = useRef(null);

  // hairline scroll-progress bar under the nav
  useEffect(() => {
    const onScroll = () => {
      const b = barRef.current; if (!b) return;
      const h = document.documentElement.scrollHeight - window.innerHeight;
      b.style.transform = `scaleX(${h > 0 ? Math.min(1, window.scrollY / h) : 0})`;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current; if (!root) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { threshold: 0.16 });
    root.querySelectorAll('[data-reveal]').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div ref={rootRef} style={{ background: `linear-gradient(170deg, ${CREAM} 55%, #FBF6EF 100%)`, maxWidth: '30rem', margin: '0 auto', fontFamily: "'Inclusive Sans', system-ui, sans-serif", color: INK, minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inclusive+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap');
        * { box-sizing: border-box; }
        .tw-nl { cursor: pointer; transition: opacity 0.2s; background: none; border: none; padding: 0; font-family: 'Inclusive Sans', sans-serif; }
        .tw-nl:hover { opacity: 0.5; }
        .tw-cta:hover .tw-circle { transform: translateX(3px); }
        .tw-li { transition: color .2s; }
        .tw-li:hover { color: ${MUTE}; }
        .tw-twinkle { display: inline-block; animation: tw-twinkle 2.6s ease-in-out infinite; transform-origin: center; }
        @keyframes tw-twinkle { 0%,100%{ transform: scale(1) rotate(0); opacity:.8 } 50%{ transform: scale(1.35) rotate(90deg); opacity:1 } }
        .tw-beat { display: inline-block; animation: tw-beat 1.9s ease-in-out infinite; }
        @keyframes tw-beat { 0%,100%{ transform: scale(1) } 15%{ transform: scale(1.3) } 30%{ transform: scale(1) } }
        .pg-float { animation: pg-float 4s ease-in-out infinite; }
        @keyframes pg-float { 0%,100%{ transform: translateY(0) } 50%{ transform: translateY(-7px) } }
        .pg-heart { animation: pg-heart 2.4s ease-in-out infinite; }
        @keyframes pg-heart { 0%,100%{ transform: scale(1) } 12%{ transform: scale(1.12) } 24%{ transform: scale(1) } }
        .tw-faq { border: none; background: none; width: 100%; text-align: left; cursor: pointer; font-family: 'Inclusive Sans', sans-serif; }
        [data-reveal]{ opacity:0; transform: translateY(24px); transition: opacity .9s cubic-bezier(.16,1,.3,1), transform .9s cubic-bezier(.16,1,.3,1); }
        [data-reveal].in{ opacity:1; transform:none; }
        [data-reveal].d1{ transition-delay:.08s } [data-reveal].d2{ transition-delay:.16s } [data-reveal].d3{ transition-delay:.24s } [data-reveal].d4{ transition-delay:.32s }
        .tw-nl:focus-visible, .tw-cta:focus-visible, .tw-faq:focus-visible { outline: 2px solid ${APRICOT}; outline-offset: 3px; border-radius: 4px; }
        @media (prefers-reduced-motion: reduce){ [data-reveal]{opacity:1;transform:none} .tw-twinkle,.tw-beat,.pg-float,.pg-heart{animation:none} }
      `}</style>

      {/* NAV */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 20, background: 'rgba(255,253,248,0.9)', backdropFilter: 'blur(16px)', borderBottom: `1px solid ${HAIR}`, padding: '1.1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}><Wordmark /></div>
        <button onClick={goToApp} className="tw-nl" style={{ borderBottom: `1.5px solid ${INK}`, fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK, paddingBottom: 1 }}>
          Get your code
        </button>
        <span ref={barRef} style={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 1.5, background: APRICOT, transform: 'scaleX(0)', transformOrigin: '0 50%', transition: 'transform .1s linear', pointerEvents: 'none' }} />
      </nav>

      {/* HERO */}
      <section style={{ padding: '3.75rem 1.5rem 2.5rem' }}>
        <div data-reveal style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', border: `1px solid ${HAIR}`, borderRadius: 999, padding: '0.35rem 0.8rem', marginBottom: '1.75rem' }}>
          <span className="tw-twinkle" style={{ color: APRICOT, fontSize: '0.7rem' }}>✳</span>
          <span style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTE }}>The wedding introductions</span>
        </div>
        <h1 data-reveal className="d1" style={{ margin: 0, fontSize: 'clamp(2.7rem, 12vw, 4rem)', fontWeight: 600, lineHeight: 1.02, letterSpacing: '-0.04em', color: INK }}>
          The best place to meet someone was never an app.
        </h1>
        <p data-reveal className="d2" style={{ margin: '1.75rem 0 0', maxWidth: '21rem', fontSize: '1.1rem', fontWeight: 400, lineHeight: 1.55, color: MUTE }}>
          It was a wedding — a whole room, hand-picked by people who love you.
        </p>
        <div data-reveal className="d3" style={{ marginTop: '2.5rem', display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
          <button onClick={goToApp} className="tw-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: INK }}>
            Get your code <CircleArrow size={34} />
          </button>
          <span style={{ fontSize: '0.68rem', fontWeight: 500, color: MUTE }}>One-time €99 · live in minutes</span>
        </div>
        <button data-reveal className="d4 tw-nl" onClick={() => navigate('/')} style={{ marginTop: '1.25rem', fontSize: '0.72rem', fontWeight: 500, color: MUTE, letterSpacing: '0.01em' }}>
          Have a code? <span style={{ color: INK, fontWeight: 700, borderBottom: `1.5px solid ${INK}`, paddingBottom: 1 }}>Enter it here</span>
        </button>
      </section>

      {/* TRUST STRIP — answers the three silent objections before they form */}
      <section style={{ padding: '0 1.5rem' }}>
        <div data-reveal style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem 1rem', padding: '1rem 0', borderTop: `1px solid ${HAIR}`, borderBottom: `1px solid ${HAIR}` }}>
          {['Private to your wedding', 'Free for guests', 'GDPR compliant · EU'].map((t, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.04em', color: MUTE }}>
              <span style={{ width: 3, height: 3, borderRadius: 999, background: APRICOT, flexShrink: 0 }} />{t}
            </span>
          ))}
        </div>
      </section>

      {/* PRODUCT GLIMPSE */}
      <section style={{ padding: '2.5rem 1.5rem 3.5rem' }}>
        <div data-reveal style={{ position: 'relative' }}>
          <PhoneGlimpse />
        </div>
        <p data-reveal className="d1" style={{ margin: '2.25rem auto 0', maxWidth: '18rem', textAlign: 'center', fontSize: '0.8rem', fontWeight: 500, color: MUTE, lineHeight: 1.6 }}>
          A private room for your wedding — your single guests discover each other, match, and talk. Nobody else can see in.
        </p>
      </section>

      {/* PROOF BAND — dark ink, animated count-ups */}
      <section style={{ background: INK, padding: '3rem 1.5rem' }}>
        <div data-reveal style={{ textAlign: 'center', marginBottom: '2.25rem' }}>
          <Label light>Every wedding is a room full of maybe</Label>
        </div>
        <div data-reveal className="d1" style={{ display: 'flex', gap: '0.5rem' }}>
          <Stat target={115} label="Guests / wedding" />
          <div style={{ width: 1, background: 'rgba(255,253,248,0.14)' }} />
          <Stat target={30} suffix="%" label="Come single" />
        </div>
        <p data-reveal className="d2" style={{ margin: '2.25rem auto 0', maxWidth: '19rem', textAlign: 'center', fontSize: '0.82rem', fontWeight: 400, lineHeight: 1.6, color: 'rgba(255,253,248,0.65)' }}>
          The single people at your wedding already have the best thing in common: you. tweddi just makes the introduction.
        </p>
      </section>

      {/* THE PROBLEM — why this exists at all */}
      <section style={{ padding: '3.5rem 1.5rem', borderTop: `1px solid ${HAIR}` }}>
        <div data-reveal style={{ marginBottom: '1.5rem' }}>
          <Label>The problem</Label>
        </div>
        <h2 data-reveal className="d1" style={{ margin: '0 0 1.25rem', fontSize: 'clamp(1.9rem, 8vw, 2.5rem)', fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.03em', color: INK }}>
          They were in the same room. They never spoke.
        </h2>
        <p data-reveal className="d2" style={{ margin: 0, maxWidth: '21rem', fontSize: '0.95rem', fontWeight: 400, lineHeight: 1.6, color: MUTE }}>
          Two people your friends both love, seated four tables apart. One of them leaves early. Nobody makes the introduction — because nobody knows to.
        </p>
        <p data-reveal className="d3" style={{ margin: '1.25rem 0 0', maxWidth: '21rem', fontSize: '0.95rem', fontWeight: 500, lineHeight: 1.6, color: INK }}>
          That's the introduction tweddi makes.
        </p>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ padding: '3.5rem 1.5rem', borderTop: `1px solid ${HAIR}` }}>
        <div data-reveal style={{ marginBottom: '2.25rem' }}>
          <Label>How it works</Label>
          <h2 style={{ margin: '0.75rem 0 0', fontSize: 'clamp(1.9rem, 8vw, 2.5rem)', fontWeight: 600, lineHeight: 1.08, letterSpacing: '-0.03em', color: INK, maxWidth: '20rem' }}>
            One code. One room.
          </h2>
        </div>
        <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {[
            ['The couple gets a code', 'A bespoke code, minted for your wedding.'],
            ['Single guests opt in', 'They join privately with the code — no public profiles.'],
            ['The right people meet', 'Swipe, match, and message only within your wedding.'],
            ['Lasts beyond the wedding', 'Guests can keep matching and messaging for 30 days after the wedding.'],
          ].map(([item, sub], i) => (
            <li key={i} data-reveal className={`tw-li d${Math.min(i,3)}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1.25rem 0', borderTop: i ? `1px solid ${HAIR}` : 'none' }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: APRICOT, letterSpacing: '0.06em', minWidth: '1.4rem', flexShrink: 0, paddingTop: '0.4rem' }}>{String(i+1).padStart(2,'0')}</span>
              <span>
                <span style={{ display: 'block', fontSize: 'clamp(1.3rem, 5.5vw, 1.65rem)', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.2, color: INK }}>{item}</span>
                <span style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.82rem', fontWeight: 400, lineHeight: 1.5, color: MUTE }}>{sub}</span>
              </span>
            </li>
          ))}
        </ol>
      </section>

      {/* TESTIMONIAL */}
      <section style={{ padding: '3.5rem 1.5rem', borderTop: `1px solid ${HAIR}` }}>
        <div data-reveal>
          <span style={{ color: APRICOT, fontSize: '2.5rem', lineHeight: 0.4, fontWeight: 700 }}>“</span>
          <p style={{ margin: '0.5rem 0 0', fontSize: 'clamp(1.4rem, 6vw, 1.8rem)', fontWeight: 500, fontStyle: 'italic', letterSpacing: '-0.02em', lineHeight: 1.3, color: INK }}>
            Two of our guests matched at our wedding and never stopped talking. tweddi turned our reception into the start of their story.
          </p>
        </div>
        <div data-reveal className="d1" style={{ marginTop: '1.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ width: '2.25rem', height: '2.25rem', borderRadius: 999, background: STONE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Mark size={16} mark={INK} bg={STONE} />
          </span>
          <span style={{ fontSize: '0.72rem', fontWeight: 600, color: INK }}>Maya &amp; Daniel<span style={{ color: MUTE, fontWeight: 500 }}> · married in Lisbon</span></span>
        </div>
      </section>

      {/* WHAT HAPPENS INSIDE */}
      <section style={{ padding: '3.5rem 1.5rem', borderTop: `1px solid ${HAIR}` }}>
        <div data-reveal style={{ marginBottom: '2rem' }}>
          <Label>What happens inside <span className="tw-twinkle" style={{ color: APRICOT }}>✳</span></Label>
        </div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {['Enter the code', 'Build a profile', 'Discover guests', 'Match & message'].map((item, i) => (
            <li key={i} data-reveal className="tw-li" style={{ padding: '0.75rem 0', fontSize: 'clamp(1.5rem, 6vw, 2rem)', fontWeight: 500, letterSpacing: '-0.03em', lineHeight: 1.35, color: INK }}>{item}</li>
          ))}
          <li data-reveal style={{ padding: '0.75rem 0', fontSize: 'clamp(1.5rem, 6vw, 2rem)', fontWeight: 500, letterSpacing: '-0.03em', lineHeight: 1.35, color: INK }}>
            Toss the bouquet <span className="tw-beat" style={{ color: APRICOT, fontSize: '0.7em' }}>♥</span>
          </li>
        </ul>
      </section>

      {/* PRICING */}
      <section style={{ padding: '3.5rem 1.5rem', borderTop: `1px solid ${HAIR}` }}>
        <div data-reveal style={{ marginBottom: '1.5rem' }}>
          <Label>One flat fee</Label>
        </div>
        <div data-reveal className="d1" style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem' }}>
          <p style={{ margin: 0, fontSize: 'clamp(4.5rem, 24vw, 7rem)', fontWeight: 700, lineHeight: 1, letterSpacing: '-0.04em', color: INK }}>€99</p>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: MUTE, letterSpacing: '0.02em' }}>one-time</span>
        </div>
        <p data-reveal className="d2" style={{ margin: '1rem 0 2rem', maxWidth: '20rem', fontSize: '0.95rem', fontWeight: 400, lineHeight: 1.55, color: MUTE }}>
          Your custom wedding code — a bespoke matchmaking experience for your single guests. No subscriptions, no per-guest fees.
        </p>
        <ul data-reveal className="d2" style={{ listStyle: 'none', margin: '0 0 2.25rem', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {['A code minted for your wedding', 'Unlimited guest opt-ins', 'Live from the day you share it', 'Stays open 30 days after the wedding'].map((f, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.82rem', fontWeight: 500, color: INK }}>
              <span style={{ color: APRICOT, fontWeight: 700 }}>✓</span>{f}
            </li>
          ))}
        </ul>
        <div data-reveal className="d3">
          <button onClick={goToApp} className="tw-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: INK }}>
            Get started <CircleArrow size={32} />
          </button>
        </div>
      </section>

      {/* FAQ — objection handling */}
      <section style={{ padding: '3.5rem 1.5rem', borderTop: `1px solid ${HAIR}` }}>
        <div data-reveal style={{ marginBottom: '1.75rem' }}>
          <Label>Good to know</Label>
        </div>
        <FAQ />
      </section>

      {/* CLOSING */}
      <section style={{ padding: '4.5rem 1.5rem 4rem', borderTop: `1px solid ${HAIR}`, textAlign: 'center' }}>
        <div data-reveal style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.75rem' }}>
          <Mark size={46} mark={INK} bg={CREAM} />
        </div>
        <h2 data-reveal className="d1" style={{ margin: '0 0 2rem', fontSize: 'clamp(2.2rem, 9vw, 2.9rem)', fontWeight: 600, lineHeight: 1.05, letterSpacing: '-0.03em', color: INK }}>
          Your wedding.<br />Their beginning.
        </h2>
        <div data-reveal className="d2" style={{ display: 'flex', justifyContent: 'center' }}>
          <button onClick={goToApp} className="tw-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: INK }}>
            Get your code <CircleArrow size={34} />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.75rem 1.5rem', borderTop: `1px solid ${HAIR}`, flexWrap: 'wrap', gap: '1rem' }}>
        <div onClick={() => navigate('/')}><Wordmark /></div>
        <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center' }}>
          <button onClick={() => navigate('/privacy')} className="tw-nl" style={{ fontSize: '0.58rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTE }}>Privacy</button>
          <button onClick={() => navigate('/terms')} className="tw-nl" style={{ fontSize: '0.58rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTE }}>Terms</button>
          <span style={{ fontSize: '0.58rem', fontWeight: 500, color: 'rgba(20,19,19,0.3)', letterSpacing: '0.02em' }}>© 2026</span>
        </div>
      </footer>
    </div>
  );
}

/* Lightweight accordion FAQ */
function FAQ() {
  const items = [
    ['Is it just for single guests?', 'Yes. Guests opt in privately with your code — no one is added without choosing to join, and profiles are only visible inside your wedding.'],
    ['When should we share the code?', 'Most couples share it a few weeks ahead so guests can match before the day — but it works right up to the reception and for 30 days after.'],
    ['Do our guests need to pay?', 'Never. The couple pays one flat €99. Every guest joins free.'],
    ['What about privacy?', 'A wedding is a closed room. Matches and messages stay within your event, and data is handled under EU GDPR.'],
  ];
  const [open, setOpen] = useState(0);
  return (
    <div>
      {items.map(([q, a], i) => {
        const isOpen = open === i;
        return (
          <div key={i} data-reveal className={`d${Math.min(i,3)}`} style={{ borderTop: i ? `1px solid ${HAIR}` : 'none' }}>
            <button className="tw-faq" onClick={() => setOpen(isOpen ? -1 : i)} style={{ padding: '1.15rem 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
              <span style={{ fontSize: '1rem', fontWeight: 600, letterSpacing: '-0.01em', color: INK }}>{q}</span>
              <span style={{ flexShrink: 0, color: MUTE, transition: 'transform 0.25s cubic-bezier(.16,1,.3,1)', transform: isOpen ? 'rotate(45deg)' : 'none' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
              </span>
            </button>
            <div style={{ maxHeight: isOpen ? '10rem' : 0, overflow: 'hidden', transition: 'max-height 0.35s cubic-bezier(.16,1,.3,1)' }}>
              <p style={{ margin: '0 0 1.15rem', fontSize: '0.85rem', fontWeight: 400, lineHeight: 1.6, color: MUTE, maxWidth: '24rem' }}>{a}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
